/**
 * CONTENT SAFETY (App Store Guideline 1.2; docs/release/CONTENT_SAFETY.md) — report crew content or
 * a user to the operator, and/or block the user. Blocking hides their team-chat messages, mark
 * text and voice notes, and live crew radio from you; it takes effect immediately.
 */
import './moderation.css';
import { useState } from 'react';
import { REPORT_REASON_LABELS, REPORT_REASONS, REPORT_RESPONSE_TARGET_HOURS, type ReportContentType, type ReportReason } from '@apos/shared';
import { Button, Checkbox, Field, IconButton, Modal, Select, TextArea, toast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { useMission } from '../../state/mission';

export interface ReportTarget {
  missionId: string;
  contentType: ReportContentType;
  /** Message / mark / call id; omitted for a voice or user report. */
  contentId?: string;
  userId: string;
  /** Display name of the author or user. */
  name: string;
  /** Short quote of the reported content, shown for context. */
  excerpt?: string;
}

const WHAT: Record<ReportContentType, string> = {
  comms: 'message',
  marker: 'mark',
  broadcast: 'call',
  voice: 'crew radio',
  user: 'user',
};

/** After a block, reload the record so their content disappears now (the server already hides it). */
async function refreshAfterBlock() {
  await useMission.getState().reloadRecord();
}

export function ReportBlockDialog({ target, onClose }: { target: ReportTarget; onClose(): void }) {
  const [reason, setReason] = useState<ReportReason>('harassment');
  const [details, setDetails] = useState('');
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [busy, setBusy] = useState<null | 'report' | 'block'>(null);
  const what = WHAT[target.contentType];

  const report = async () => {
    setBusy('report');
    try {
      const r = await api.moderation.report({
        missionId: target.missionId,
        contentType: target.contentType,
        ...(target.contentId ? { contentId: target.contentId } : {}),
        reportedUserId: target.userId,
        reason,
        ...(details.trim() ? { details: details.trim() } : {}),
        alsoBlock,
      });
      if (r.blocked) await refreshAfterBlock();
      toast({
        title: r.blocked ? `Report sent and ${target.name} blocked` : 'Report sent',
        body: `Thanks. Reports are reviewed within ${r.responseTargetHours} hours.`,
        tone: 'success',
      });
      onClose();
    } catch (e) {
      toast({ title: 'Report not sent', body: errorMessage(e), tone: 'alert' });
    } finally {
      setBusy(null);
    }
  };

  const block = async () => {
    setBusy('block');
    try {
      await api.moderation.block(target.userId);
      await refreshAfterBlock();
      toast({ title: `${target.name} blocked`, body: 'You will no longer see their messages, marks or crew radio. Unblock in Settings.', tone: 'success' });
      onClose();
    } catch (e) {
      toast({ title: 'Could not block', body: errorMessage(e), tone: 'alert' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      locked={busy !== null}
      size="sm"
      eyebrow="Report or block"
      title={target.contentType === 'user' ? `Report ${target.name}` : `Report ${what} from ${target.name}`}
      testId="report-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy !== null}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void block()} loading={busy === 'block'} disabled={busy !== null} data-testid="report-block-only">
            Block only
          </Button>
          <Button variant="primary" onClick={() => void report()} loading={busy === 'report'} disabled={busy !== null} data-testid="report-send">
            Send report
          </Button>
        </>
      }
    >
      {target.excerpt ? (
        <blockquote className="report-dialog__quote" data-testid="report-excerpt">
          {target.excerpt}
        </blockquote>
      ) : null}
      <Field label="Reason">
        {(id) => (
          <Select id={id} value={reason} onChange={(e) => setReason(e.target.value as ReportReason)}>
            {REPORT_REASONS.map((r) => (
              <option key={r} value={r}>
                {REPORT_REASON_LABELS[r]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Details" optional>
        {(id) => <TextArea id={id} rows={3} maxLength={1000} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Anything that helps us review it" />}
      </Field>
      <Checkbox checked={alsoBlock} onChange={setAlsoBlock} label={`Also block ${target.name}`} />
      <p className="muted small">
        The operator reviews every report within {REPORT_RESPONSE_TARGET_HOURS} hours and can remove the content or the user. {target.name} is not told who reported
        them.
      </p>
    </Modal>
  );
}

/** Small flag button that opens the report/block dialog for one item. */
export function ReportButton({ target, className, label }: { target: ReportTarget; className?: string; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton
        icon="flag"
        size="sm"
        iconSize={14}
        className={className ?? 'report-btn'}
        label={label ?? `Report or block ${target.name}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        data-testid="report-open"
      />
      {open ? <ReportBlockDialog target={target} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
